import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import axios from "axios";
import {
  useParams,
  useNavigate,
  useOutletContext,
  useLocation,
} from "react-router-dom";
import { AuthContext } from "../components/AuthContext/AuthContext";
import ChatSection from "../components/ChatSection/ChatSection";
import TextInput from "../components/TextInput/TextInput";
import ChatIcon from "../../src/resources/images/Navigation/Chat.svg";
import Avatar from "../resources/images/icons/robot.svg";
import userAvatar from "../resources/images/icons/users.png";
import "../pagesStyling/ChatPrompt.css";
import { useSelectedCollections } from "../components/SelectedCollectionsContext/SelectedCollectionsContext";
import { API_BASE_URL } from "../apiConfig";

interface Message {
  type: "bot" | "user";
  message: string;
  sender?: string;
  avatarPath?: string;
  userIconPath?: string;
  sources?: {
    name: string;
    url?: string;
    relevance_score: number;
    file_id?: string;
    page?: number;
    source_type?: string;
  }[];
  score?: string;
}

interface Collection {
  [key: string]: string;
}

interface ChatHistoryResponse {
  chat_history: {
    created_at: string;
    prompt: string;
    response: string;
    sources: {
      name: string;
      url?: string;
      relevance_score: number;
      file_id?: string;
      page?: number;
      source_type?: string;
    }[];
    score: {
      correctness: number;
      comprehensiveness: number;
      readability: number;
    };
  }[];
  collections: Collection[];
}

const WELCOME_MESSAGE: Message = {
  type: "bot",
  message:
    "Welcome to the Diabetes Care Assistant chat tool! Let's get started!",
  sender: "DCA Chatbot",
  avatarPath: Avatar,
};

const LOADING_MESSAGE: Message = {
  type: "bot",
  message: "typing...",
  sender: "DCA Chatbot",
  avatarPath: Avatar,
};

function ChatPrompt() {
  const { token } = useContext(AuthContext) || {};
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { setChats, chats } = useOutletContext<{
    setChats: React.Dispatch<React.SetStateAction<any[]>>;
    chats: any[];
  }>();
  const { selectedCollections } = useSelectedCollections();

  const [messages, setMessages] = useState<Message[]>([WELCOME_MESSAGE]);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [selectedCollectionNames, setSelectedCollectionNames] = useState<
    string[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [chatCollections, setChatCollections] = useState<Collection[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  const textInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (selectedCollections.length > 0) {
      const names = selectedCollections.map((collection) => collection.name);
      setSelectedCollectionNames(names);
    } else {
      setSelectedCollectionNames([]);
    }
  }, [selectedCollections]);

  useEffect(() => {
    if (textInputRef.current) {
      textInputRef.current.focus();
    }
  }, []);

  const fetchChatDetails = useCallback(
    async (chatId: string) => {
      try {
        const response = await axios.get<ChatHistoryResponse>(
          `${API_BASE_URL}/chat/history/single?uuid=${chatId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            withCredentials: true,
          }
        );

        const data = response.data;
        const historyMessages =
          data.chat_history?.length > 0
            ? data.chat_history.flatMap((entry) => [
                {
                  type: "user" as const,
                  message: entry.prompt,
                  sender: "You",
                  userIconPath: userAvatar,
                },
                {
                  type: "bot" as const,
                  message: entry.response,
                  sender: "DCA Chatbot",
                  avatarPath: Avatar,
                  sources: entry.sources,
                  score: entry.score?.correctness.toString(),
                },
              ])
            : [];

        setMessages([WELCOME_MESSAGE, ...historyMessages]);
        setChatCollections(data.collections || []);
      } catch (error) {
        console.error("Error fetching chat details:", error);
        setMessages([WELCOME_MESSAGE]);
        setChatCollections([]);
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          console.warn("Chat history not found, likely a new chat");
        }
      }
    },
    [token]
  );

  const fetchChatHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/chat/history`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      const data = response.data;
      const formattedChats = (data.previous_chats || []).map((chat: any) => {
        const topic = chat.first_prompt || "Untitled Chat";
        const truncatedTopic =
          topic.length > 15 ? topic.substring(0, 15) + "..." : topic;
        return {
          id: chat.uuid,
          chatTopic: truncatedTopic,
          chatText: truncatedTopic || "",
          creationDate: new Date(chat.created_at),
        };
      });

      setChats(formattedChats);
    } catch (error) {
      console.error("Error fetching chat history:", error);
    }
  }, [token, setChats]);

  useEffect(() => {
    if (!token || !id || isInitialized) return;

    const initialIsNewChat = location.state?.isNewChat || false;
    if (initialIsNewChat) {
      setMessages([WELCOME_MESSAGE]);
      setChatCollections(
        selectedCollections.map((col) => ({ uuid: col.uuid, name: col.name }))
      );
      navigate(location.pathname, { replace: true, state: {} });
    } else {
      fetchChatDetails(id);
    }

    setCurrentChatId(id);
    setIsInitialized(true);
  }, [
    id,
    token,
    fetchChatDetails,
    location.state,
    selectedCollections,
    navigate,
  ]);

  const handleChatSubmit = useCallback(
    async (message: string) => {
      if (!token || !id) {
        console.error("No token or chat ID available for API call.");
        return;
      }

      const userMessage: Message = {
        type: "user",
        message,
        sender: "You",
        userIconPath: userAvatar,
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);

      try {
        const response = await axios.post(
          `${API_BASE_URL}/chat/session`,
          { prompt: message, chat_uuid: id },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          }
        );

        const data = response.data;
        const botMessage: Message = {
          type: "bot",
          message: data.answer || "I'm sorry, I couldn't process that.",
          sender: "DCA Chatbot",
          avatarPath: Avatar,
          sources: data.sources,
          score: data.score?.correctness.toString(),
        };

        setMessages((prev) => [
          ...prev.filter((msg) => msg !== LOADING_MESSAGE),
          botMessage,
        ]);
        setIsLoading(false);

        if (messages.length === 1) {
          await fetchChatHistory();
        }
      } catch (error) {
        console.error("Error during chat API call:", error);
        setMessages((prev) => [
          ...prev.filter((msg) => msg !== LOADING_MESSAGE),
          {
            type: "bot",
            message: "Sorry, something went wrong. Please try again.",
            sender: "DCA Chatbot",
            avatarPath: Avatar,
          },
        ]);
        setIsLoading(false);
      }
    },
    [token, id, messages, fetchChatHistory]
  );

  return (
    <div className="chat-details">
      <div className="chat-interface">
        <ChatSection
          messages={isLoading ? [...messages, LOADING_MESSAGE] : messages}
          modelName={
            selectedCollectionNames.join(", ") || "All data collections"
          }
          collections={chatCollections}
        />
        <div className="chat-input-container">
          <TextInput
            ref={textInputRef}
            iconPath={ChatIcon}
            placeholder="Write something..."
            onSubmit={handleChatSubmit}
          />
        </div>
      </div>
    </div>
  );
}

export default ChatPrompt;
